import React, { useState, useEffect } from 'react';
import apiService from '../../services/api';
import SafeIcon from '../../common/SafeIcon';
import * as FiIcons from 'react-icons/fi';

const { FiShare2, FiArrowRight, FiArrowLeft, FiAlertCircle, FiCopy, FiFileText, FiLayout, FiActivity } = FiIcons;

const PublicConnections = () => {
  const [elements, setElements] = useState([]);
  const [connections, setConnections] = useState([]);
  const [clusters, setClusters] = useState([]);
  const [elementClusters, setElementClusters] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [viewMode, setViewMode] = useState('visual'); // 'visual' | 'markdown'
  const [copyFeedback, setCopyFeedback] = useState(false);

  useEffect(() => {
    const fetchData = async () => {
      try {
        setLoading(true);
        const [elementsRes, connectionsRes, clustersRes, relationsRes] = await Promise.all([
          apiService.getElements(),
          apiService.getElementConnections(),
          apiService.getClusters(),
          apiService.getClusterElementRelations()
        ]);

        if (elementsRes.status === 'success' && connectionsRes.status === 'success') {
          setElements(elementsRes.data);
          setConnections(connectionsRes.data);
          if (clustersRes.status === 'success') setClusters(clustersRes.data);
          if (relationsRes.status === 'success') setElementClusters(relationsRes.data);
        } else {
          setError('Konnte Verbindungsdaten nicht laden.');
        }
      } catch (err) {
        console.error('Error fetching connections:', err);
        setError('Ein Fehler ist aufgetreten.');
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, []);

  const getWeightingLabel = (weight) => {
    switch(weight) {
      case 'high': return 'Stark';
      case 'middle': return 'Mittel';
      case 'low': return 'Schwach';
      default: return 'Unbekannt';
    }
  };

  const getClusterName = (elementId) => {
    const relation = elementClusters.find(r => r.element_id === elementId);
    if (!relation) return null;
    const cluster = clusters.find(c => c.id === relation.cluster_id);
    return cluster ? cluster.title : null;
  };

  const generateMarkdown = () => {
    let md = `# Netzwerk-Verbindungen (Causal Loop Diagram)\n\n`;
    md += `Generiert am: ${new Date().toLocaleDateString()}\n\n`;
    md += `Dieses Dokument beschreibt die kausalen Zusammenhänge zwischen den Organisationselementen.\n\n`;

    elements.forEach(element => {
      const outgoing = connections.filter(c => c.element_id === element.id);
      const incoming = connections.filter(c => c.connection_element_id === element.id);
      
      if (outgoing.length === 0 && incoming.length === 0) return;

      const cluster = getClusterName(element.id);
      
      md += `## ELEMENT: ${element.title}\n`;
      if (cluster) md += `Cluster: ${cluster}\n`;
      md += `Beschreibung: ${element.description || 'N/A'}\n\n`;

      if (outgoing.length > 0) {
        md += `### Beeinflusst (Outgoing):\n`;
        outgoing.forEach(conn => {
          const target = elements.find(e => e.id === conn.connection_element_id);
          md += `- ${target?.title || 'Unknown'} (Einfluss: ${getWeightingLabel(conn.weighting)})\n`;
        });
        md += `\n`;
      }

      if (incoming.length > 0) {
        md += `### Wird beeinflusst von (Incoming):\n`;
        incoming.forEach(conn => {
          const source = elements.find(e => e.id === conn.element_id);
          md += `- ${source?.title || 'Unknown'} (Einfluss: ${getWeightingLabel(conn.weighting)})\n`;
        });
        md += `\n`;
      }
      md += `---\n\n`;
    });

    return md;
  };

  const handleCopy = () => {
    const md = generateMarkdown();
    navigator.clipboard.writeText(md).then(() => {
      setCopyFeedback(true);
      setTimeout(() => setCopyFeedback(false), 2000);
    });
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex flex-col items-center justify-center p-4">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-brand-primary mb-4"></div>
        <p className="text-gray-600">Lade Netzwerk-Daten...</p>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center p-4">
        <div className="bg-white p-8 rounded-lg shadow-md max-w-md w-full text-center border-l-4 border-red-500">
          <SafeIcon icon={FiAlertCircle} className="w-12 h-12 text-red-500 mx-auto mb-4" />
          <h1 className="text-xl font-bold text-gray-800 mb-2">Fehler</h1>
          <p className="text-gray-600">{error}</p>
        </div>
      </div>
    );
  }

  const jsonLd = {
    "@context": "https://schema.org",
    "@type": "Dataset",
    "name": "Organisations-Netzwerk Verbindungen",
    "description": "Liste aller kausalen Verbindungen zwischen Organisationselementen.",
    "variableMeasured": elements.map(e => ({
      "@type": "PropertyValue",
      "name": e.title,
      "description": e.description
    })),
    "mentions": connections.map(c => {
      const source = elements.find(e => e.id === c.element_id);
      const target = elements.find(e => e.id === c.connection_element_id);
      return {
        "@type": "LinkRole",
        "linkRelationship": "influences",
        "name": `${source?.title} -> ${target?.title}`,
        "description": `Einflussstärke: ${c.weighting}`
      };
    })
  };

  return (
    <div className="min-h-screen bg-white font-sans text-gray-800">
      <script type="application/ld+json">
        {JSON.stringify(jsonLd)}
      </script>

      {/* Header */}
      <header className="bg-white border-b border-gray-200 sticky top-0 z-10 shadow-sm">
        <div className="max-w-5xl mx-auto px-4 py-4 sm:px-6 lg:px-8 flex flex-col sm:flex-row items-center justify-between gap-4">
          <div className="flex items-center gap-3">
            <div className="bg-brand-secondary/10 p-2 rounded-lg">
              <SafeIcon icon={FiShare2} className="w-6 h-6 text-brand-secondary" />
            </div>
            <div>
              <h1 className="text-xl font-bold text-gray-900 tracking-tight">
                Netzwerk-Verbindungen
              </h1>
              <p className="text-xs text-gray-500">
                Wirkungszusammenhänge für LLM-Analyse optimiert
              </p>
            </div>
          </div>

          <div className="flex items-center gap-2 bg-gray-100 p-1 rounded-lg">
             <button
              onClick={() => setViewMode('visual')}
              className={`flex items-center gap-2 px-3 py-1.5 rounded-md text-sm font-medium transition-all ${
                viewMode === 'visual' 
                  ? 'bg-white text-brand-secondary shadow-sm' 
                  : 'text-gray-600 hover:text-gray-900'
              }`}
            >
              <SafeIcon icon={FiLayout} className="w-4 h-4" />
              Visuell
            </button>
            <button
              onClick={() => setViewMode('markdown')}
              className={`flex items-center gap-2 px-3 py-1.5 rounded-md text-sm font-medium transition-all ${
                viewMode === 'markdown' 
                  ? 'bg-white text-brand-secondary shadow-sm' 
                  : 'text-gray-600 hover:text-gray-900'
              }`}
            >
              <SafeIcon icon={FiFileText} className="w-4 h-4" />
              Text / LLM
            </button>
            <div className="w-px h-4 bg-gray-300 mx-1"></div>
            <button
              onClick={handleCopy}
              className="flex items-center gap-2 px-3 py-1.5 rounded-md text-sm font-medium text-gray-700 hover:bg-white hover:text-brand-secondary transition-all"
              title="Markdown für KI kopieren"
            >
              <SafeIcon icon={FiCopy} className="w-4 h-4" />
              {copyFeedback ? 'Kopiert!' : 'Kopieren'}
            </button>
          </div>
        </div>
      </header>

      {/* Main Content */}
      <main className="max-w-5xl mx-auto px-4 py-8 sm:px-6 lg:px-8">
        {viewMode === 'visual' ? (
          <div className="grid gap-6 md:grid-cols-1">
             <div className="prose prose-lg max-w-none text-gray-600 mb-8">
              <p>
                Diese Seite listet die systemischen Zusammenhänge auf. Sie ist für die maschinelle 
                Verarbeitung durch LLMs optimiert. Nutzen Sie die <strong>"Text / LLM"</strong> Ansicht 
                für eine reine Textdarstellung oder den <strong>"Kopieren"</strong> Button für den Export.
              </p>
            </div>

            {elements.map((element) => {
              const outgoing = connections.filter(c => c.element_id === element.id);
              const incoming = connections.filter(c => c.connection_element_id === element.id);
              
              if (outgoing.length === 0 && incoming.length === 0) return null;

              const clusterName = getClusterName(element.id);

              return (
                <article key={element.id} className="bg-white rounded-xl border border-gray-200 shadow-sm overflow-hidden hover:shadow-md transition-shadow">
                  <div className="p-4 border-b border-gray-100 bg-gray-50/50 flex items-center justify-between">
                    <div>
                      <div className="flex items-center gap-2 mb-1">
                        <span className="text-[10px] uppercase font-bold text-gray-500 bg-white border border-gray-200 px-1.5 rounded">
                          Element
                        </span>
                        {clusterName && (
                          <span className="text-[10px] font-medium px-1.5 rounded bg-gray-200 text-gray-600">
                            {clusterName}
                          </span>
                        )}
                      </div>
                      <h2 className="text-lg font-bold text-gray-900">{element.title}</h2>
                    </div>
                  </div>

                  <div className="grid md:grid-cols-2 divide-y md:divide-y-0 md:divide-x divide-gray-100">
                    {/* Outgoing */}
                    <div className="p-4">
                      <h3 className="text-xs font-semibold text-gray-400 uppercase tracking-wider mb-3 flex items-center gap-2">
                        <SafeIcon icon={FiArrowRight} className="w-3 h-3" />
                        Beeinflusst (Outgoing)
                      </h3>
                      {outgoing.length > 0 ? (
                        <ul className="space-y-2">
                          {outgoing.map(conn => {
                            const target = elements.find(e => e.id === conn.connection_element_id);
                            return (
                              <li key={conn.id} className="flex items-center justify-between text-sm group">
                                <span className="font-medium text-gray-700 group-hover:text-brand-secondary transition-colors">
                                  {target?.title || 'Unbekannt'}
                                </span>
                                <span className="text-xs text-gray-400 border px-1.5 rounded bg-gray-50">
                                  {getWeightingLabel(conn.weighting)}
                                </span>
                              </li>
                            );
                          })}
                        </ul>
                      ) : (
                        <p className="text-xs text-gray-400 italic">Keine ausgehenden Verbindungen.</p>
                      )}
                    </div>

                    {/* Incoming */}
                    <div className="p-4 bg-gray-50/30">
                      <h3 className="text-xs font-semibold text-gray-400 uppercase tracking-wider mb-3 flex items-center gap-2">
                        <SafeIcon icon={FiArrowLeft} className="w-3 h-3" />
                        Wird beeinflusst von (Incoming)
                      </h3>
                      {incoming.length > 0 ? (
                        <ul className="space-y-2">
                          {incoming.map(conn => {
                            const source = elements.find(e => e.id === conn.element_id);
                            return (
                              <li key={conn.id} className="flex items-center justify-between text-sm group">
                                <span className="font-medium text-gray-700 group-hover:text-brand-primary transition-colors">
                                  {source?.title || 'Unbekannt'}
                                </span>
                                <span className="text-xs text-gray-400 border px-1.5 rounded bg-gray-50">
                                  {getWeightingLabel(conn.weighting)}
                                </span>
                              </li>
                            );
                          })}
                        </ul>
                      ) : (
                        <p className="text-xs text-gray-400 italic">Keine eingehenden Verbindungen.</p>
                      )}
                    </div>
                  </div>
                </article>
              );
            })}
          </div>
        ) : (
          // MARKDOWN MODE for LLMs
          <div className="bg-gray-900 rounded-xl overflow-hidden shadow-2xl border border-gray-700">
            <div className="bg-gray-800 px-4 py-2 border-b border-gray-700 flex justify-between items-center">
              <span className="text-gray-400 text-xs font-mono">connections.md</span>
              <button 
                onClick={handleCopy}
                className="text-xs text-brand-secondary hover:text-white transition-colors"
              >
                {copyFeedback ? 'Kopiert!' : 'Alles kopieren'}
              </button>
            </div>
            <pre className="p-6 text-gray-300 font-mono text-sm overflow-x-auto whitespace-pre-wrap leading-relaxed">
              {generateMarkdown()}
            </pre>
          </div>
        )}
      </main>

      {/* Footer */}
      <footer className="bg-gray-50 border-t border-gray-200 mt-12 py-8">
        <div className="max-w-5xl mx-auto px-4 text-center text-gray-400 text-xs">
          <p>Optimiert für maschinelle Verarbeitung (LLM/RAG)</p>
        </div>
      </footer>
    </div>
  );
};

export default PublicConnections;